/**
 * Achievement Definitions
 *
 * Shared achievement definitions used by both firebase-gamification.ts and backend-gamification.ts
 */

export interface Achievement {
  id: string;
  name: string;
  description: string;
  category: 'time' | 'productivity' | 'collaboration' | 'streak' | 'quality' | 'hidden' | 'elite';
  icon: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary' | 'secret';
  xpReward: number;
  unlockCondition: string;
  maxProgress: number;
  resetType: 'weekly' | 'biweekly' | 'monthly' | 'continuous';
  hidden: boolean;
}

export const ACHIEVEMENTS_DEFINITIONS: Achievement[] = [
  // ========================================
  // MONTHLY: TIME & CONSISTENCY (12 badges)
  // ========================================
  {
    id: 'time_apprentice',
    name: 'Time Apprentice',
    description: 'Log 20+ hours this month',
    category: 'time',
    icon: 'clock',
    rarity: 'common',
    xpReward: 50,
    unlockCondition: 'Log 20 or more hours in a single month',
    maxProgress: 20,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'time_professional',
    name: 'Time Professional',
    description: 'Log 40+ hours this month',
    category: 'time',
    icon: 'clock-4',
    rarity: 'rare',
    xpReward: 100,
    unlockCondition: 'Log 40 or more hours in a single month',
    maxProgress: 40,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'time_titan',
    name: 'Time Titan',
    description: 'Log 80+ hours this month',
    category: 'time',
    icon: 'timer',
    rarity: 'epic',
    xpReward: 300,
    unlockCondition: 'Log 80 or more hours in a single month',
    maxProgress: 80,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'time_legend',
    name: 'Time Legend',
    description: 'Log 120+ hours this month',
    category: 'time',
    icon: 'award',
    rarity: 'legendary',
    xpReward: 500,
    unlockCondition: 'Log 120 or more hours in a single month',
    maxProgress: 120,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'early_bird',
    name: 'Early Bird',
    description: 'Log before 9 AM on 5 days',
    category: 'time',
    icon: 'sunrise',
    rarity: 'common',
    xpReward: 50,
    unlockCondition: 'Log time before 9 AM for 5 different days',
    maxProgress: 5,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'morning_hustler',
    name: 'Morning Hustler',
    description: 'Log before 9 AM on 15 days',
    category: 'time',
    icon: 'sun',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Log time before 9 AM for 15 different days',
    maxProgress: 15,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Log after 8 PM on 5 days',
    category: 'time',
    icon: 'moon',
    rarity: 'common',
    xpReward: 50,
    unlockCondition: 'Log time after 8 PM for 5 different days',
    maxProgress: 5,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: '7-day working streak',
    category: 'streak',
    icon: 'flame',
    rarity: 'common',
    xpReward: 75,
    unlockCondition: 'Log activity for 7 consecutive working days',
    maxProgress: 7,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'fortnight_fighter',
    name: 'Fortnight Fighter',
    description: '14-day working streak',
    category: 'streak',
    icon: 'dumbbell',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Log activity for 14 consecutive working days',
    maxProgress: 14,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'monthly_marathon',
    name: 'Monthly Marathon',
    description: '20-day working streak',
    category: 'streak',
    icon: 'person-standing',
    rarity: 'epic',
    xpReward: 250,
    unlockCondition: 'Log activity for 20 consecutive working days',
    maxProgress: 20,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'consistent_logger',
    name: 'Consistent Logger',
    description: 'Log on 20+ days this month',
    category: 'time',
    icon: 'calendar-check',
    rarity: 'rare',
    xpReward: 100,
    unlockCondition: 'Log time on 20 or more different days in a month',
    maxProgress: 20,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'perfect_attendance',
    name: 'Perfect Attendance',
    description: 'Log every working day (Mon-Fri)',
    category: 'time',
    icon: 'calendar-days',
    rarity: 'epic',
    xpReward: 300,
    unlockCondition: 'Log time every working day in a month (~22 days)',
    maxProgress: 22,
    resetType: 'monthly',
    hidden: false,
  },

  // ========================================
  // MONTHLY: PRODUCTIVITY & TASKS (10 badges)
  // ========================================
  {
    id: 'task_starter',
    name: 'Task Starter',
    description: 'Close 5+ issues this month',
    category: 'productivity',
    icon: 'check-circle',
    rarity: 'common',
    xpReward: 50,
    unlockCondition: 'Close 5 or more issues in a single month',
    maxProgress: 5,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'task_crusher',
    name: 'Task Crusher',
    description: 'Close 15+ issues this month',
    category: 'productivity',
    icon: 'check-circle-2',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Close 15 or more issues in a single month',
    maxProgress: 15,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'task_terminator',
    name: 'Task Terminator',
    description: 'Close 30+ issues this month',
    category: 'productivity',
    icon: 'check-square',
    rarity: 'epic',
    xpReward: 300,
    unlockCondition: 'Close 30 or more issues in a single month',
    maxProgress: 30,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'task_annihilator',
    name: 'Task Annihilator',
    description: 'Close 50+ issues this month',
    category: 'productivity',
    icon: 'zap',
    rarity: 'legendary',
    xpReward: 500,
    unlockCondition: 'Close 50 or more issues in a single month',
    maxProgress: 50,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'bug_finder',
    name: 'Bug Finder',
    description: 'Close 3+ bugs this month',
    category: 'productivity',
    icon: 'bug',
    rarity: 'common',
    xpReward: 75,
    unlockCondition: 'Close 3 or more bug-type issues in a month',
    maxProgress: 3,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'bug_buster',
    name: 'Bug Buster',
    description: 'Close 10+ bugs this month',
    category: 'productivity',
    icon: 'bug-off',
    rarity: 'rare',
    xpReward: 200,
    unlockCondition: 'Close 10 or more bug-type issues in a month',
    maxProgress: 10,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'bug_exterminator',
    name: 'Bug Exterminator',
    description: 'Close 20+ bugs this month',
    category: 'productivity',
    icon: 'shield-check',
    rarity: 'epic',
    xpReward: 400,
    unlockCondition: 'Close 20 or more bug-type issues in a month',
    maxProgress: 20,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'quick_closer',
    name: 'Quick Closer',
    description: 'Close 5 issues under estimate',
    category: 'productivity',
    icon: 'fast-forward',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Complete 5 issues faster than estimated this month',
    maxProgress: 5,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'efficiency_ace',
    name: 'Efficiency Ace',
    description: 'Close 10 issues under estimate',
    category: 'productivity',
    icon: 'trending-up',
    rarity: 'epic',
    xpReward: 300,
    unlockCondition: 'Complete 10 issues faster than estimated this month',
    maxProgress: 10,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'issue_machine',
    name: 'Issue Machine',
    description: 'Create 20+ issues this month',
    category: 'productivity',
    icon: 'file-plus',
    rarity: 'rare',
    xpReward: 100,
    unlockCondition: 'Create 20 or more issues in a single month',
    maxProgress: 20,
    resetType: 'monthly',
    hidden: false,
  },

  // ========================================
  // MONTHLY: QUALITY & DETAIL (5 badges)
  // ========================================
  {
    id: 'detail_oriented',
    name: 'Detail Oriented',
    description: '15 logs with >50 char comments',
    category: 'quality',
    icon: 'text',
    rarity: 'common',
    xpReward: 50,
    unlockCondition: 'Write 15 time log descriptions longer than 50 characters',
    maxProgress: 15,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'description_wizard',
    name: 'Description Wizard',
    description: '20 logs with >100 char comments',
    category: 'quality',
    icon: 'pen-line',
    rarity: 'rare',
    xpReward: 100,
    unlockCondition: 'Write 20 time log descriptions longer than 100 characters',
    maxProgress: 20,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'wordsmith',
    name: 'Wordsmith',
    description: 'Create 5 issues with >500 char descriptions',
    category: 'quality',
    icon: 'file-text',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Create 5 issues with descriptions longer than 500 characters',
    maxProgress: 5,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'novelist',
    name: 'Novelist',
    description: 'Create issue with >2000 char description',
    category: 'quality',
    icon: 'book-open',
    rarity: 'epic',
    xpReward: 200,
    unlockCondition: 'Create one issue with description longer than 2000 characters',
    maxProgress: 1,
    resetType: 'monthly',
    hidden: false,
  },
  {
    id: 'commenter',
    name: 'Commenter',
    description: 'Add 30+ comments on issues',
    category: 'quality',
    icon: 'message-circle',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Post 30 or more comments on issues this month',
    maxProgress: 30,
    resetType: 'monthly',
    hidden: false,
  },

  // ========================================
  // MONTHLY: HIDDEN/FUN (3 badges)
  // ========================================
  {
    id: 'lucky_7',
    name: 'Lucky 7',
    description: 'Earn exactly 777 XP this month',
    category: 'hidden',
    icon: 'clover',
    rarity: 'secret',
    xpReward: 77,
    unlockCondition: 'Earn exactly 777 XP in a single month',
    maxProgress: 777,
    resetType: 'monthly',
    hidden: true,
  },
  {
    id: 'overachiever',
    name: 'Overachiever',
    description: 'Earn 2000+ XP this month',
    category: 'hidden',
    icon: 'trophy',
    rarity: 'legendary',
    xpReward: 500,
    unlockCondition: 'Earn 2000 or more XP in a single month',
    maxProgress: 2000,
    resetType: 'monthly',
    hidden: true,
  },
  {
    id: 'variety_pack',
    name: 'Variety Pack',
    description: 'Close 1 bug + 1 task + 1 feature',
    category: 'hidden',
    icon: 'package',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Close at least one issue of each type: bug, task, and feature',
    maxProgress: 3,
    resetType: 'monthly',
    hidden: true,
  },

  // ========================================
  // LIFETIME: TIME MILESTONES (5 badges)
  // ========================================
  {
    id: 'time_novice',
    name: 'Time Novice',
    description: 'Log 100 hours total',
    category: 'time',
    icon: 'watch',
    rarity: 'common',
    xpReward: 100,
    unlockCondition: 'Log 100 hours across all time',
    maxProgress: 100,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'time_journeyman',
    name: 'Time Journeyman',
    description: 'Log 250 hours total',
    category: 'time',
    icon: 'alarm-clock',
    rarity: 'rare',
    xpReward: 200,
    unlockCondition: 'Log 250 hours across all time',
    maxProgress: 250,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'time_master',
    name: 'Time Master',
    description: 'Log 500 hours total',
    category: 'time',
    icon: 'clock-3',
    rarity: 'epic',
    xpReward: 500,
    unlockCondition: 'Log 500 hours across all time',
    maxProgress: 500,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'time_grandmaster',
    name: 'Time Grandmaster',
    description: 'Log 1000 hours total',
    category: 'time',
    icon: 'crown',
    rarity: 'legendary',
    xpReward: 1000,
    unlockCondition: 'Log 1000 hours across all time',
    maxProgress: 1000,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'centurion',
    name: 'Centurion',
    description: '100-day working streak',
    category: 'streak',
    icon: 'medal',
    rarity: 'legendary',
    xpReward: 1000,
    unlockCondition: 'Maintain a 100 working day streak',
    maxProgress: 100,
    resetType: 'continuous',
    hidden: false,
  },

  // ========================================
  // LIFETIME: PRODUCTIVITY MILESTONES (8 badges)
  // ========================================
  {
    id: 'issue_creator',
    name: 'Issue Creator',
    description: 'Create 50 issues total',
    category: 'productivity',
    icon: 'clipboard-list',
    rarity: 'common',
    xpReward: 100,
    unlockCondition: 'Create 50 total issues',
    maxProgress: 50,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'issue_generator',
    name: 'Issue Generator',
    description: 'Create 100 issues total',
    category: 'productivity',
    icon: 'list',
    rarity: 'rare',
    xpReward: 200,
    unlockCondition: 'Create 100 total issues',
    maxProgress: 100,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'issue_factory',
    name: 'Issue Factory',
    description: 'Create 250 issues total',
    category: 'productivity',
    icon: 'factory',
    rarity: 'epic',
    xpReward: 500,
    unlockCondition: 'Create 250 total issues',
    maxProgress: 250,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'task_slayer',
    name: 'Task Slayer',
    description: 'Close 100 issues total',
    category: 'productivity',
    icon: 'check',
    rarity: 'common',
    xpReward: 150,
    unlockCondition: 'Close 100 total issues',
    maxProgress: 100,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'task_destroyer',
    name: 'Task Destroyer',
    description: 'Close 250 issues total',
    category: 'productivity',
    icon: 'target',
    rarity: 'rare',
    xpReward: 300,
    unlockCondition: 'Close 250 total issues',
    maxProgress: 250,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'task_obliterator',
    name: 'Task Obliterator',
    description: 'Close 500 issues total',
    category: 'productivity',
    icon: 'bomb',
    rarity: 'epic',
    xpReward: 600,
    unlockCondition: 'Close 500 total issues',
    maxProgress: 500,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'bug_slayer',
    name: 'Bug Slayer',
    description: 'Close 25 bugs total',
    category: 'productivity',
    icon: 'shield',
    rarity: 'rare',
    xpReward: 200,
    unlockCondition: 'Close 25 total bug issues',
    maxProgress: 25,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'bug_nemesis',
    name: 'Bug Nemesis',
    description: 'Close 50 bugs total',
    category: 'productivity',
    icon: 'shield-alert',
    rarity: 'epic',
    xpReward: 400,
    unlockCondition: 'Close 50 total bug issues',
    maxProgress: 50,
    resetType: 'continuous',
    hidden: false,
  },

  // ========================================
  // LIFETIME: QUALITY MILESTONES (4 badges)
  // ========================================
  {
    id: 'efficiency_expert',
    name: 'Efficiency Expert',
    description: 'Close 10 under estimate total',
    category: 'productivity',
    icon: 'zap-off',
    rarity: 'rare',
    xpReward: 150,
    unlockCondition: 'Complete 10 total issues under estimated time',
    maxProgress: 10,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'efficiency_master',
    name: 'Efficiency Master',
    description: 'Close 25 under estimate total',
    category: 'productivity',
    icon: 'gauge',
    rarity: 'epic',
    xpReward: 350,
    unlockCondition: 'Complete 25 total issues under estimated time',
    maxProgress: 25,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'log_keeper',
    name: 'Log Keeper',
    description: '500 time entries total',
    category: 'quality',
    icon: 'scroll',
    rarity: 'rare',
    xpReward: 200,
    unlockCondition: 'Create 500 total time entries',
    maxProgress: 500,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'chronicler',
    name: 'Chronicler',
    description: '1000 time entries total',
    category: 'quality',
    icon: 'book',
    rarity: 'epic',
    xpReward: 500,
    unlockCondition: 'Create 1000 total time entries',
    maxProgress: 1000,
    resetType: 'continuous',
    hidden: false,
  },

  // ========================================
  // LIFETIME: ELITE/LEGENDARY (3 badges)
  // ========================================
  {
    id: 'xp_millionaire',
    name: 'XP Millionaire',
    description: 'Earn 10,000 lifetime XP',
    category: 'elite',
    icon: 'gem',
    rarity: 'legendary',
    xpReward: 2000,
    unlockCondition: 'Earn 10,000 total XP across all time',
    maxProgress: 10000,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'dedication',
    name: 'Dedication',
    description: 'Active for 180 days',
    category: 'elite',
    icon: 'heart',
    rarity: 'legendary',
    xpReward: 1500,
    unlockCondition: 'Be active for 180 total days',
    maxProgress: 180,
    resetType: 'continuous',
    hidden: false,
  },
  {
    id: 'hall_of_fame',
    name: 'Hall of Fame',
    description: 'Reach level 10',
    category: 'elite',
    icon: 'star',
    rarity: 'legendary',
    xpReward: 2500,
    unlockCondition: 'Reach level 10',
    maxProgress: 10,
    resetType: 'continuous',
    hidden: false,
  },
];

/**
 * Get monthly achievements (reset every month)
 */
export function getMonthlyAchievements(): Achievement[] {
  return ACHIEVEMENTS_DEFINITIONS.filter(
    (a) => a.resetType === 'monthly' || a.resetType === 'weekly' || a.resetType === 'biweekly'
  );
}

/**
 * Get lifetime achievements (never reset)
 */
export function getLifetimeAchievements(): Achievement[] {
  return ACHIEVEMENTS_DEFINITIONS.filter((a) => a.resetType === 'continuous');
}

/**
 * Calculate days until next monthly reset
 */
export function getDaysUntilMonthlyReset(): number {
  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const diffTime = nextMonth.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
